import { useEffect, useState, useCallback } from "react";
import { Loader2, Trophy, X, Film, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { WinnerBanner, SellerLeaderboard } from "@/components/SquareExtras";

import { IMG_W, IMG_H, FLYER_SRC, cellRect } from "@/lib/boardGrid";

function cellStyle(num) {
  const { x, y, w, h } = cellRect(num);
  return {
    left: `${(x / IMG_W) * 100}%`, top: `${(y / IMG_H) * 100}%`,
    width: `${(w / IMG_W) * 100}%`, height: `${(h / IMG_H) * 100}%`,
  };
}

function nameFor(board, num) {
  const sq = board?.squares?.find((s) => s.number === num);
  return sq?.name || null;
}

export default function SquareBoard({ embedded = false }) {
  const [board, setBoard] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sel, setSel] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/squares/board")
      .then((r) => { if (r.data && r.data.id) setBoard(r.data); else setNotFound(true); })
      .catch(() => setNotFound(true));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { localStorage.setItem("i65_board_ref", ref.toUpperCase()); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("release");
    if (embedded || !token) { return; }
    api.post("/squares/release", { token }).then((r) => { if (r.data?.released) { toast("Checkout cancelled, your square is open again"); } load(); }).catch(() => {});
    window.history.replaceState(null, "", window.location.pathname);
  }, [embedded, load]);

  if (notFound) return embedded ? null : <div className="min-h-[60vh] flex items-center justify-center text-[#a9a196]">No active board right now.</div>;
  if (!board) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#e0481f]" /></div>;

  const { stats, winners = {}, status } = board;
  const closed = status !== "open";
  const pct = stats.total ? Math.round((stats.sold / stats.total) * 100) : 0;
  const quarters = [["q1", "1st"], ["q2", "2nd"], ["q3", "3rd"], ["q4", "4th"]];
  const winnerNums = Object.values(winners).filter((n) => n != null);

  const openBuy = (num) => { setSel(num); setName(""); setEmail(""); setPhone(""); };

  const buy = async () => {
    if (!name.trim() || !email.trim() || !phone.trim()) { toast.error("Enter your name, email and phone"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/squares/checkout", {
        number: sel, name: name.trim(), email: email.trim(), phone: phone.trim(), origin_url: window.location.origin,
        ref_code: localStorage.getItem("i65_board_ref") || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div data-testid={embedded ? "home-square-board" : "square-board-page"}>
      {/* Flyer board with live squares */}
      <WinnerBanner board={board} />
      <div className="bg-black">
        <div className="relative mx-auto w-full max-w-[720px] [container-type:inline-size]" data-testid="square-grid">
          <img src={FLYER_SRC} alt={`${board.title} — ${board.team_a} vs ${board.team_b}, $${board.price} per square`} className="block w-full h-auto select-none" draggable={false} data-testid="square-board-flyer" />
          {board.squares.map((sq) => {
            const isWinner = winnerNums.includes(sq.number);
            const pos = cellStyle(sq.number);
            const base = "absolute select-none";
            const face = (bg, extra = "") => `absolute inset-[0.3cqw] flex flex-col items-center text-center overflow-hidden ${bg} ${extra}`;
            const num = (color) => <span className={`font-display font-bold leading-none mt-[8%] ${color}`} style={{ fontSize: "2.7cqw" }}>{sq.number}</span>;
            if (sq.status === "sold") {
              return (
                <div key={sq.number} style={pos} className={base} data-testid={`square-${sq.number}`} title={sq.name || "Sold"}>
                  <div className={face("bg-[#f3c649]", isWinner ? "ring-[0.5cqw] ring-inset ring-[#c4473d]" : "")}>
                    {isWinner && <Trophy className="absolute top-[6%] right-[6%] text-[#c4473d] w-[2.2cqw] h-[2.2cqw]" />}
                    {num("text-[#1a1208]")}
                    <span className="w-full px-[3%] mt-auto mb-[6%] font-display font-bold uppercase leading-[1.05] text-black break-words line-clamp-2" style={{ fontSize: "2.05cqw" }}>{sq.name || "SOLD"}</span>
                  </div>
                </div>
              );
            }
            if (sq.status === "pending") {
              return (
                <div key={sq.number} style={pos} className={`${base} cursor-not-allowed`} data-testid={`square-${sq.number}`}>
                  <div className={face("bg-[#fde2b0]")}>
                    {num("text-[#7a4a00]")}
                    <span className="mt-auto mb-[8%] font-display font-bold uppercase text-[#7a4a00]" style={{ fontSize: "1.9cqw" }}>Held</span>
                  </div>
                </div>
              );
            }
            return (
              <button key={sq.number} style={pos} onClick={() => !closed && openBuy(sq.number)} disabled={closed}
                className={`${base} group ${closed ? "cursor-not-allowed" : "cursor-pointer"}`}
                aria-label={`Buy square ${sq.number}`} data-testid={`square-${sq.number}`}>
                <span className={face("bg-white", closed ? "" : "transition-colors duration-150 group-hover:bg-[#ffe1d6]")}>{num("text-black")}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative overflow-hidden border-b border-[#d4af37]/15">
        <div className="relative z-10 max-w-5xl mx-auto px-5 py-8 text-center">
          <p className="text-[#d6cec2] text-sm" data-testid="square-price-pill">Tap any open square on the board to grab it for <span className="gold-text">${board.price}</span>. Updates live as squares sell.</p>

          {/* Progress */}
          <div className="mt-5 max-w-md mx-auto">
            <div className="flex justify-center text-xs text-[#a9a196] mb-1.5 font-display uppercase tracking-wide">
              <span data-testid="square-claimed-count">{stats.sold} / {stats.total} claimed</span>
            </div>
            <div className="h-2 rounded-full bg-[#2a2622] overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#e0481f] to-[#f3c649] transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-10 space-y-10">
        {closed && (
          <div className="flex items-center gap-2 justify-center rounded-xl bg-[#c4473d]/15 border border-[#c4473d]/30 text-[#f0b8b2] px-4 py-3 font-display uppercase tracking-wide text-sm" data-testid="square-closed-banner">
            <Lock size={16} /> This board is closed — all squares are locked.
          </div>
        )}

        {/* Numbers draw video */}
        {board.numbers_video && (
          <div data-testid="square-video">
            <h2 className="headline text-2xl text-[#f7f3ec] flex items-center gap-2 mb-3"><Film size={20} className="text-[#f3c649]" /> The Numbers Are In</h2>
            <div className="rounded-2xl overflow-hidden border border-[#d4af37]/20 bg-black">
              <video src={board.numbers_video} controls playsInline className="w-full max-h-[70vh] bg-black" />
            </div>
          </div>
        )}

        {/* Winners */}
        {winnerNums.length > 0 && (
          <div>
            <h2 className="headline text-2xl text-[#f7f3ec] flex items-center gap-2 mb-3"><Trophy size={20} className="text-[#f3c649]" /> Quarter Winners</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {quarters.map(([q, label]) => {
                const n = winners[q];
                return (
                  <div key={q} className={`rounded-xl p-4 text-center border ${n != null ? "bg-[#d4af37]/10 border-[#d4af37]/40" : "bg-[#1a1816] border-[#3a332c]"}`} data-testid={`winner-${q}`}>
                    <p className="eyebrow text-xs text-[#d4af37] mb-1">{label} Quarter</p>
                    {n != null ? (
                      <>
                        <p className="headline text-2xl text-[#f7f3ec]">#{n}</p>
                        <p className="text-[#d6cec2] text-sm truncate">{nameFor(board, n) || "—"}</p>
                      </>
                    ) : <p className="text-[#8f857a] text-sm mt-2">Pending</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <SellerLeaderboard leaders={board.leaderboard} />

      </div>

      {/* Buy modal */}
      {sel != null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm" onClick={() => !loading && setSel(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-[#1a1816] border border-[#d4af37]/25 p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="square-buy-modal">
            <button onClick={() => !loading && setSel(null)} className="absolute top-4 right-4 text-[#8f857a] hover:text-[#f7f3ec]"><X size={18} /></button>
            <p className="eyebrow text-xs text-[#d4af37]">Claim Your Square</p>
            <h3 className="headline text-4xl text-[#f7f3ec] mt-1">#{sel}</h3>
            <p className="text-[#a9a196] text-sm mt-1 mb-5">{board.title} · <span className="gold-text">${board.price}</span></p>
            <div className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (shown on the square)" className="w-full bg-[#121110] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="square-buyer-name" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email for confirmation" className="w-full bg-[#121110] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="square-buyer-email" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" autoComplete="tel" placeholder="Phone number" className="w-full bg-[#121110] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="square-buyer-phone" />
              <button onClick={buy} disabled={loading} className="btn-gold w-full rounded-full px-6 py-3.5 font-display uppercase tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="square-buy-submit">
                {loading ? <Loader2 size={18} className="animate-spin" /> : null} Pay ${board.price}
              </button>
            </div>
            <p className="text-[#6b645b] text-xs text-center mt-3">Secure checkout via Stripe</p>
          </div>
        </div>
      )}
    </div>
  );
}
